import React, { useEffect, useState } from "react";
import { waLink } from "@/lib/refresh";
import { compartilharTexto } from "@/lib/whatsapp";
import api, { brl, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ChevronLeft, ChevronRight, Loader2, BadgeDollarSign, Share2, CheckCircle2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const WEEK = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const pad = (n) => String(n).padStart(2, "0");
const compact = (v) => brl(v).replace(/^R\$\s?/, "");
const compactMobile = (v) => (v >= 1000 ? compact(v).replace(/,\d{2}$/, "") : compact(v));

export const InstallmentsCalendar = ({ clienteId, onBaixa, refreshKey }) => {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const [ym, setYm] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(todayIso);
  const [lembrete, setLembrete] = useState(false);

  const mes = `${ym.y}-${pad(ym.m + 1)}`;
  useEffect(() => {
    setLoading(true);
    api.get(`/installments?mes=${mes}${clienteId ? `&cliente_id=${clienteId}` : ""}`).then((r) => { setItems(r.data); setLoading(false); });
  }, [mes, clienteId, refreshKey]);

  const byDay = {};
  items.forEach((p) => { (byDay[p.vencimento] = byDay[p.vencimento] || []).push(p); });
  const first = new Date(ym.y, ym.m, 1).getDay();
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => `${mes}-${pad(i + 1)}`)];
  const move = (d) => setYm(({ y, m }) => { const dt = new Date(y, m + d, 1); return { y: dt.getFullYear(), m: dt.getMonth() }; });
  const sel = byDay[day] || [];
  const tone = (list) => list.some((p) => p.status === "vencido") ? "bg-red-500" : list.every((p) => p.status === "pago" || p.status === "renegociado") ? "bg-emerald-500" : "bg-gold";

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-4 min-w-0" data-testid="inst-calendar">
      <div className="trt-card p-4 cal-card min-w-0 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <Button size="icon" variant="ghost" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-bold" data-testid="cal-month">{MESES[ym.m]} {ym.y}</div>
          <Button size="icon" variant="ghost" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="cal-scroll min-w-0">
          <div className="grid grid-cols-7 text-center text-[11px] uppercase text-slate-400 mb-1 cal-grid">{WEEK.map((w) => <div key={w}>{w}</div>)}</div>
          {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div> : (
            <div className="grid grid-cols-7 gap-1 cal-grid">
              {cells.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const list = byDay[d] || [];
                const total = list.reduce((a, p) => a + Number(p.valor || 0), 0);
                const isSel = d === day;
                return (
                  <button key={d} onClick={() => setDay(d)} data-testid={`cal-day-${d}`} title={list.length ? `${list.length} parcela(s) • ${brl(total)}` : undefined}
                    className={`min-h-[64px] min-w-0 rounded-lg p-1.5 text-left border transition-colors overflow-hidden ${isSel ? "border-gold bg-amber-50" : "border-transparent hover:bg-slate-50"} ${d === todayIso ? "ring-1 ring-gold/60" : ""}`}>
                    <div className={`text-xs font-semibold cal-daynum ${d === todayIso ? "gold-text" : "text-slate-600"}`}>{Number(d.slice(-2))}</div>
                    {list.length > 0 && (
                      <div className="mt-1">
                        <span className={`inline-block text-[10px] leading-4 text-white rounded px-1 cal-count ${tone(list)}`}>{list.length}</span>
                        <div className="text-[10px] mono text-slate-600 num-nowrap" data-testid={`cal-total-${d}`}><span className="lg:hidden">{compactMobile(total)}</span><span className="hidden lg:inline">{compact(total)}</span></div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex gap-3 text-[11px] text-slate-500 mt-3">
          <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm bg-gold inline-block" /> A vencer</span>
          <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" /> Com atraso</span>
          <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Tudo pago</span>
        </div>
      </div>
      <div className="trt-card p-4" data-testid="cal-day-panel">
        <div className="font-bold mb-1">{fmtDate(day)}</div>
        <div className="text-xs text-slate-500 mb-3">{sel.length} parcela(s) • {brl(sel.reduce((a, p) => a + Number(p.valor || 0), 0))}</div>
        {onBaixa && sel.some((p) => p.status !== "pago" && p.status !== "renegociado" && p.cliente_telefone) && (
          <Button size="sm" variant="outline" onClick={() => setLembrete(true)} data-testid="cal-lembrete-btn"
            className="w-full mb-3 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
            <Share2 className="w-4 h-4" /> Enviar lembretes do dia ({sel.filter((p) => p.status !== "pago" && p.status !== "renegociado" && p.cliente_telefone).length})
          </Button>
        )}
        {sel.length === 0 ? <p className="text-slate-400 text-sm">Nenhuma parcela neste dia.</p> : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto slim-scroll">
            {sel.map((p) => (
              <div key={p.id} className="rounded-lg border p-3 text-sm" data-testid={`cal-inst-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0"><div className="font-semibold truncate">{p.cliente_nome}</div><div className="text-xs text-slate-500">Parcela #{p.numero}</div></div>
                  <div className="text-right"><div className="font-bold mono">{brl(p.valor)}</div><StatusBadge type="inst" status={p.status} vencimento={p.vencimento} /></div>
                </div>
                {onBaixa && p.status !== "pago" && p.status !== "renegociado" && (
                  <Button size="sm" onClick={() => onBaixa(p)} data-testid={`cal-baixa-${p.id}`}
                    className="mt-2 w-full bg-gold hover:bg-[hsl(var(--gold-hover))] text-black font-semibold gap-1"><BadgeDollarSign className="w-4 h-4" /> DAR BAIXA</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {lembrete && <LembretesDiaDialog day={day} onClose={() => setLembrete(false)} />}
    </div>
  );
};

const LembretesDiaDialog = ({ day, onClose }) => {
  const [items, setItems] = useState(null);
  const [sent, setSent] = useState({});
  const [preview, setPreview] = useState(null);
  useEffect(() => { api.get("/cobranca/lembretes-dia", { params: { data: day } }).then((r) => setItems(r.data)).catch(() => setItems([])); }, [day]);
  const send = async (c) => {
    // Mesmo comportamento do Comunicar/baixa: abre a conversa do cliente no WhatsApp (wa.me) com o texto preenchido; envio manual.
    const url = waLink(c.telefone_normalizado, c.texto);
    if (!url) { toast.error(`${c.cliente_nome}: telefone/WhatsApp ausente ou inválido. Corrija o cadastro do cliente.`); setSent((s) => ({ ...s, [c.cliente_id]: "invalido" })); return; }
    window.open(url, "_blank", "noopener");
    try { await api.post("/mensagens/registrar", { cliente_id: c.cliente_id, titulo: "Lembrete de parcela", texto: c.texto, telefone: c.telefone, evento: "lembrete_dia", categoria: "Lembrete", status: "aberta_whatsapp" }); } catch { /* histórico opcional */ }
    setSent((s) => ({ ...s, [c.cliente_id]: true }));
  };
  const next = () => { const c = (items || []).find((x) => !sent[x.cliente_id]); if (c) send(c); };
  const done = Object.keys(sent).length;
  const total = items?.length || 0;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lembretes-dia-dialog">
        <DialogHeader>
          <DialogTitle>Lembretes de {fmtDate(day)}</DialogTitle>
          <DialogDescription>{total} cliente(s) com parcela em aberto. Um lembrete por cliente (contratos agrupados), usando o modelo “Lembrete do dia” configurado em Mensagens. Ao tocar, a conversa do cliente abre no WhatsApp com o texto preenchido — você só confirma o envio.</DialogDescription>
        </DialogHeader>
        {!items ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div> : (<>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${(done / Math.max(1, total)) * 100}%` }} /></div>
          <div className="max-h-64 overflow-y-auto divide-y rounded-lg border text-sm" data-testid="lembretes-lista">
            {items.map((c) => (
              <div key={c.cliente_id} className="flex items-center gap-2 px-3 py-2" data-testid={`lembrete-item-${c.cliente_id}`}>
                {sent[c.cliente_id] === true ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <MessageCircle className={`w-4 h-4 shrink-0 ${c.telefone_valido ? "text-slate-300" : "text-red-400"}`} />}
                <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setPreview(preview === c.cliente_id ? null : c.cliente_id)}>
                  <div className="font-medium truncate">{c.cliente_nome}</div>
                  <div className="text-xs text-slate-500">{c.quantidade} parcela(s) • {c.contratos} contrato(s){c.telefone_valido ? <span className="mono"> • {c.telefone}</span> : <span className="text-red-600 font-semibold" data-testid={`lembrete-sem-telefone-${c.cliente_id}`}> • sem WhatsApp válido</span>}</div>
                </button>
                <Button size="sm" variant="ghost" className="text-emerald-600 disabled:opacity-40" disabled={!c.telefone_valido} onClick={() => send(c)} data-testid={`lembrete-send-${c.cliente_id}`} title={c.telefone_valido ? `Abrir WhatsApp de ${c.cliente_nome}` : "Telefone ausente ou inválido"}><MessageCircle className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => compartilharTexto({ texto: c.texto, titulo: "Lembrete de parcela", cliente_id: c.cliente_id, telefone: c.telefone, evento: "lembrete_dia", categoria: "Lembrete" })} data-testid={`lembrete-share-${c.cliente_id}`} title="Compartilhar/copiar (outro aplicativo)"><Share2 className="w-4 h-4" /></Button>
              </div>
            ))}
            {total === 0 && <div className="px-3 py-6 text-center text-slate-400">Nenhuma parcela em aberto neste dia.</div>}
          </div>
          {preview && <pre className="text-xs whitespace-pre-wrap rounded-lg bg-emerald-50 border border-emerald-100 p-3 max-h-48 overflow-y-auto" data-testid="lembrete-preview">{items.find((x) => x.cliente_id === preview)?.texto}</pre>}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>Fechar</Button>
            <Button onClick={next} disabled={done >= total} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="lembrete-next-btn"><MessageCircle className="w-4 h-4" /> Enviar próximo ({done}/{total})</Button>
          </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
